import React, { useMemo, useState } from "react";
import { CheckCircle2, Info, Loader2, ShieldAlert, X } from "lucide-react";

/**
 * Read-only visual diff card shown after `/admin/full-restore-preview`.
 *
 * The operator sees, per collection, what the actual restore would do:
 *   • in_backup    — docs in the uploaded file
 *   • currently    — docs in the DB right now
 *   • will_insert  — new rows the restore will create
 *   • will_update  — rows the backup will overwrite (matching key)
 *   • will_skip    — audit-log rows dropped because they have no
 *                    stable key and the target isn't empty
 *   • will_seed    — same rows above but the target IS empty so they
 *                    seed instead of being skipped
 *
 * Collections that exist in the DB but aren't in the backup are shown
 * separately as "orphans" — the restore leaves them alone; this list
 * exists so nobody gets surprised by "why are supplements still here".
 */
export default function RestorePreviewCard({ preview, busy, onApply, onCancel }) {
  const [showZero, setShowZero] = useState(false);

  const rows = useMemo(() => {
    const raw = Object.entries(preview?.collections || {})
      .map(([name, c]) => ({ name, ...c }));
    // Rows sort by "will_something totals" descending so the interesting
    // collections float to the top.
    return raw.sort((a, b) => {
      const impactA = a.will_insert + a.will_update + a.will_seed;
      const impactB = b.will_insert + b.will_update + b.will_seed;
      return impactB - impactA;
    });
  }, [preview]);

  const filtered = showZero ? rows : rows.filter((r) => (
    r.in_backup > 0 || r.currently > 0
  ));

  const totals = useMemo(() => rows.reduce((acc, r) => ({
    in_backup: acc.in_backup + r.in_backup,
    will_insert: acc.will_insert + r.will_insert,
    will_update: acc.will_update + r.will_update,
    will_skip: acc.will_skip + r.will_skip,
    will_seed: acc.will_seed + r.will_seed,
  }), { in_backup: 0, will_insert: 0, will_update: 0, will_skip: 0, will_seed: 0 }), [rows]);

  return (
    <div
      className="mt-4 border border-cyan-500/40 rounded-lg bg-[#0A1424]"
      data-testid="restore-preview-card"
    >
      <div className="flex items-start justify-between p-4 pb-2 border-b border-[#1E304B]">
        <div className="flex items-center gap-2">
          <ShieldAlert size={16} className="text-cyan-300" />
          <div>
            <div className="text-sm font-display text-slate-100">
              Restore preview — {preview.filename || "backup"}
            </div>
            <div className="text-[10.5px] text-slate-500 mt-0.5 font-mono-data">
              backup stamp: {preview.backup_stamp || "—"} · schema {preview.schema}
            </div>
          </div>
        </div>
        <button
          onClick={onCancel}
          className="pill !p-1.5 text-slate-400"
          disabled={busy}
          data-testid="restore-preview-cancel"
        >
          <X size={14} />
        </button>
      </div>

      {/* Top-line summary */}
      <div className="grid grid-cols-4 gap-2 p-4 text-center">
        <SummaryTile label="Insert"  value={totals.will_insert} color="text-emerald-300" />
        <SummaryTile label="Update"  value={totals.will_update} color="text-cyan-300" />
        <SummaryTile label="Seed"    value={totals.will_seed}   color="text-blue-300" />
        <SummaryTile label="Skip"    value={totals.will_skip}   color="text-slate-400"
                     hint={totals.will_skip ? "no stable key + non-empty target" : ""} />
      </div>

      {/* Collection table */}
      <div className="px-4 pb-2 max-h-[280px] overflow-y-auto">
        <table className="w-full text-[11.5px]">
          <thead className="sticky top-0 bg-[#0A1424] text-slate-500 uppercase tracking-widest text-[9.5px]">
            <tr>
              <th className="text-left py-1">Collection</th>
              <th className="text-right py-1">In backup</th>
              <th className="text-right py-1">Currently</th>
              <th className="text-right py-1 text-emerald-400">Insert</th>
              <th className="text-right py-1 text-cyan-400">Update</th>
              <th className="text-right py-1 text-blue-400">Seed</th>
              <th className="text-right py-1 text-slate-400">Skip</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.name} className="border-t border-[#111e33]">
                <td className="py-1 font-mono-data text-slate-200">
                  {r.name}
                  {r.special_merge && (
                    <span
                      className="ml-1.5 text-[8.5px] uppercase tracking-widest bg-violet-500/20 text-violet-200 border border-violet-500/40 rounded px-1"
                      title="Uses specialised merge logic (dedup by name+dob for sailors, key=email for users, etc.)"
                    >
                      special
                    </span>
                  )}
                  {r.dedup_name_dob_match !== undefined && (
                    <span className="ml-1 text-[10px] text-slate-500">
                      · dedup match {r.dedup_name_dob_match} / new {r.dedup_new_athletes}
                    </span>
                  )}
                </td>
                <td className="py-1 text-right font-mono-data text-slate-300">{r.in_backup}</td>
                <td className="py-1 text-right font-mono-data text-slate-400">{r.currently}</td>
                <td className={`py-1 text-right font-mono-data ${r.will_insert ? "text-emerald-300" : "text-slate-600"}`}>
                  {r.will_insert || "—"}
                </td>
                <td className={`py-1 text-right font-mono-data ${r.will_update ? "text-cyan-300" : "text-slate-600"}`}>
                  {r.will_update || "—"}
                </td>
                <td className={`py-1 text-right font-mono-data ${r.will_seed ? "text-blue-300" : "text-slate-600"}`}>
                  {r.will_seed || "—"}
                </td>
                <td className={`py-1 text-right font-mono-data ${r.will_skip ? "text-slate-300" : "text-slate-600"}`}>
                  {r.will_skip || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > filtered.length && (
          <button
            className="mt-1 text-[10px] text-slate-500 hover:text-slate-300"
            onClick={() => setShowZero(true)}
          >
            + show {rows.length - filtered.length} empty collections
          </button>
        )}
      </div>

      {/* PDFs section — only when the uploaded ZIP contained gridfs/ entries. */}
      {preview.pdf_total ? (
        <div className="mx-4 mb-3 px-3 py-2 border border-cyan-500/30 bg-cyan-500/5 rounded" data-testid="restore-preview-pdfs">
          <div className="text-[10px] uppercase tracking-widest text-cyan-300 mb-1.5">
            PDF binaries in ZIP · {preview.pdf_total} files
          </div>
          <table className="w-full text-[11px] font-mono-data">
            <thead className="text-[9.5px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left">Bucket</th>
                <th className="text-right">In ZIP</th>
                <th className="text-right text-cyan-300">Would insert</th>
                <th className="text-right text-slate-400">Already present</th>
                <th className="text-right text-rose-300">Bad ObjectId</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(preview.pdfs || {}).map(([bucket, row]) => (
                <tr key={bucket} className="border-t border-[#111e33]">
                  <td className="py-1 text-slate-200">{bucket}</td>
                  <td className="py-1 text-right text-slate-300">{row.in_zip}</td>
                  <td className={`py-1 text-right ${row.would_insert ? "text-cyan-200" : "text-slate-600"}`}>
                    {row.would_insert || "—"}
                  </td>
                  <td className={`py-1 text-right ${row.already_present ? "text-slate-300" : "text-slate-600"}`}>
                    {row.already_present || "—"}
                  </td>
                  <td className={`py-1 text-right ${row.bad_oid ? "text-rose-300" : "text-slate-600"}`}>
                    {row.bad_oid || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Orphans note */}
      {Array.isArray(preview.orphans) && preview.orphans.length > 0 && (
        <div className="mx-4 mb-3 px-3 py-2 border border-amber-500/30 bg-amber-500/10 rounded text-[11px] text-amber-200">
          <Info size={11} className="inline -mt-0.5 mr-1" />
          {preview.orphans.length} live collection{preview.orphans.length === 1 ? "" : "s"} not in the backup —
          restore will <b>leave them untouched</b>:{" "}
          <span className="font-mono-data text-amber-100">
            {preview.orphans.map((o) => `${o.collection} (${o.docs})`).join(", ")}
          </span>
        </div>
      )}

      {/* Apply / cancel */}
      <div className="flex items-center justify-end gap-2 px-4 pb-4 border-t border-[#1E304B] pt-3">
        <button
          onClick={onCancel}
          disabled={busy}
          className="pill text-slate-300"
          data-testid="restore-preview-cancel-btn"
        >
          <X size={13} /> Cancel
        </button>
        <button
          onClick={onApply}
          disabled={busy}
          className="pill active"
          style={{ background: "rgba(16,185,129,0.15)", borderColor: "#10B981", color: "#6EE7B7" }}
          data-testid="restore-preview-apply-btn"
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
          Apply restore
        </button>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, color, hint }) {
  return (
    <div className="rounded border border-[#1E304B] bg-[#0B1627] py-2" title={hint || ""}>
      <div className={`font-mono-data text-lg ${color}`}>{value.toLocaleString()}</div>
      <div className="text-[9px] uppercase tracking-widest text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}
